<template>
    <div class="login-container">
        <!-- 背景图区域 -->
        <div class="login-bg">
            <img src="@/assets/img/login-bg2.jpg" alt="登录背景" class="login-bg-img" />
        </div>

        <!-- 右侧登录卡片 -->
        <div class="login-card-wrapper">
            <div class="login-card">
                <h2 class="login-title">系统登录</h2>

                <!-- 登录方式切换 -->
                <div class="login-login-tabs">
                    <button
                            class="login-tab-btn"
                            :class="{ 'login-active': loginType === 'userPwd' }"
                            @click="loginType = 'userPwd'"
                    >
                        密码登录
                    </button>
                    <button
                            class="login-tab-btn"
                            :class="{ 'login-active': loginType === 'code' }"
                            @click="loginType = 'code'"
                    >
                        验证码登录
                    </button>
                </div>

                <!-- 登录表单 -->
                <form class="login-form" @submit.prevent="handleLogin">
                    <!-- 账号错误错误提示框 - 统一显示错误提示 -->
                    <div
                            v-if="globalError"
                            class="login-global-error"
                            :class="{ 'login-login-error-shake': shakeTrigger }"
                            :key="shakeTrigger"
                    >
                        <span>{{ globalError }}</span>
                    </div>

                    <!-- 手机号输入框 -->
                    <div class="login-form-item" v-if="loginType === 'userPwd'">
                        <label class="login-form-label">手机号</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="text"
                                    v-model="form.phoneNumber"
                                    placeholder="请输入手机号"
                                    class="login-form-input"
                                    maxlength="11"
                                    ref="phoneNumberInput"
                                    @input="handleInput('phoneNumber')"
                                    @blur="validateField('phoneNumber')"
                                    @focus="showTooltip($event, errorText.phoneNumber)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.phoneNumber" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.phoneNumber }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 邮箱输入框 -->
                    <div class="login-form-item" v-if="loginType === 'code'">
                        <label class="login-form-label">邮箱</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="text"
                                    v-model="form.email"
                                    placeholder="请输入邮箱"
                                    class="login-form-input"
                                    maxlength="100"
                                    ref="emailInput"
                                    @input="handleInput('email')"
                                    @blur="validateField('email')"
                                    @focus="showTooltip($event, errorText.email)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.email" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.email }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 密码输入框 - 密码登录方式显示 -->
                    <div class="login-form-item" v-if="loginType === 'userPwd'">
                        <label class="login-form-label">密码</label>
                        <div class="login-input-wrapper">
                            <input
                                    type="password"
                                    v-model="form.userPwd"
                                    placeholder="请输入密码"
                                    class="login-form-input"
                                    maxlength="20"
                                    ref="userPwdInput"
                                    @input="handleInput('userPwd')"
                                    @blur="validateField('userPwd')"
                                    @focus="showTooltip($event, errorText.userPwd)"
                            />
                            <transition name="login-tooltip-fade">
                                <div v-if="fieldError.userPwd" class="login-error-tooltip">
                                    <span class="login-tooltip-text">{{ errorText.userPwd }}</span>
                                    <div class="login-tooltip-arrow"></div>
                                </div>
                            </transition>
                        </div>
                    </div>

                    <!-- 验证码输入框 + 获取按钮 - 验证码登录方式显示 -->
                    <div class="login-form-item" v-if="loginType === 'code'">
                        <label class="login-form-label">验证码</label>
                        <div class="login-input-with-button">
                            <input
                                    type="text"
                                    v-model="form.code"
                                    placeholder="请输入验证码"
                                    class="login-form-input login-code-input"
                                    maxlength="6"
                                    ref="codeInput"
                                    @input="handleInput('code')"
                                    @blur="validateField('code')"
                                    @focus="showTooltip($event, errorText.code)"
                            />
                            <button
                                    type="button"
                                    class="login-get-code-btn"
                                    @click="getVerificationCode"
                                    :disabled="isCountingDown || !isEmailValid"
                            >
                                {{ isCountingDown ? `${countDown}s后重新获取` : '获取验证码' }}
                            </button>
                        </div>
                        <transition name="login-tooltip-fade">
                            <div v-if="fieldError.code" class="login-error-tooltip login-code-tooltip">
                                <span class="login-tooltip-text">{{ errorText.code }}</span>
                                <div class="login-tooltip-arrow"></div>
                            </div>
                        </transition>
                    </div>

                    <!-- 登录按钮 -->
                    <div class="login-login-form-actions">
                        <button type="submit" class="login-submit-btn login-login-btn" :disabled="!isAllValid">
                            <span v-if="!isLoading">登录</span>
                            <span v-if="isLoading" class="login-loading-spinner">⟳</span>
                        </button>
                    </div>

                    <!-- 注册 + 找回密码 按钮行 -->
                    <div class="login-links">
                        <button type="button" class="login-link-btn " @click="goToRegister">注册</button>
                        <button type="button" class="login-link-btn " @click="goToFindPwd">找回密码</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { useLoginLogic } from './login.js'

    const {
        form,
        fieldError,
        errorText,
        globalError,
        shakeTrigger,
        isLoading,
        handleLogin,
        goToRegister,
        goToFindPwd,
        handleInput,
        validateField,
        phoneNumberInput,
        emailInput,
        userPwdInput,
        codeInput,
        loginType,
        isCountingDown,
        countDown,
        getVerificationCode,
        isAllValid,
        isEmailValid
    } = useLoginLogic()

    // 自定义气泡提示显示逻辑
    const showTooltip = (e, text) => {
        if (!text) return
        e.target.setAttribute('title', text)
    }
</script>

<style scoped>
    /*@import 引用是全局的，需要通过不同命名来隔离，相同命名的css会根据先后加载顺序被覆盖*/
    @import '../../assets/css/login.css';
</style>